import { useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChipsProps {
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  tone?: 'brand' | 'accent' | 'success' | 'danger' | 'neutral'
  maxLength?: number
  disabled?: boolean
  /** Retorna tooltip se o valor está conflitando (ex: presente em outra classe). */
  getWarning?: (value: string) => string | null
}

const tones = {
  brand: 'bg-brand-50 text-brand-700 border-brand-100',
  accent: 'bg-accent-50 text-accent-700 border-accent-100',
  success: 'bg-success-bg text-success border-success/20',
  danger: 'bg-danger-bg text-danger border-danger/20',
  neutral: 'bg-neutral-50 text-fg-2 border-border',
} as const

const warningTone = 'bg-warning-bg text-warning border-warning/40'

/** Splits raw text by "," and any newline, trims, drops empties. */
function splitChipsInput(raw: string): string[] {
  return raw
    .split(/[,\n\r]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

export function Chips({
  values,
  onChange,
  placeholder,
  tone = 'neutral',
  maxLength = 120,
  disabled,
  getWarning,
}: ChipsProps) {
  const [input, setInput] = useState('')

  function pushMany(items: string[]) {
    if (items.length === 0) return
    const seen = new Set(values)
    const next = [...values]
    for (const it of items) {
      if (seen.has(it)) continue
      next.push(it)
      seen.add(it)
    }
    if (next.length !== values.length) onChange(next)
  }

  function commit() {
    const items = splitChipsInput(input)
    pushMany(items)
    setInput('')
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Backspace' && input === '' && values.length) {
      onChange(values.slice(0, -1))
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text')
    if (!pasted) return
    if (/[,\n\r]/.test(pasted)) {
      e.preventDefault()
      const combined = (input + pasted).trim()
      pushMany(splitChipsInput(combined))
      setInput('')
    }
  }

  return (
    <div
      className={cn(
        'flex flex-wrap gap-1.5 px-2.5 py-2 min-h-[40px] rounded-md border bg-bg-surface',
        'focus-within:border-border-focus focus-within:ring-2 focus-within:ring-brand-500/20',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      )}
    >
      {values.map((v) => {
        const warning = getWarning?.(v) ?? null
        return (
          <span
            key={v}
            title={warning ?? undefined}
            className={cn(
              'inline-flex items-center gap-1 px-2 h-6 rounded-pill text-xs font-medium border',
              warning ? warningTone : tones[tone],
            )}
          >
            {warning && <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />}
            <span>{v}</span>
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              disabled={disabled}
              className="opacity-60 hover:opacity-100 transition-opacity"
              aria-label={`Remover ${v}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )
      })}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKey}
        onPaste={onPaste}
        onBlur={commit}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={values.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[140px] outline-none text-base text-fg-1 placeholder:text-fg-3 bg-transparent"
      />
    </div>
  )
}
